<script setup>
// NOTICE: KAPTION-INTEGRATION
// Full-page Gmail-style WhatsApp Campaign Builder
import { ref, computed, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert, useTrack } from 'dashboard/composables';
import { CAMPAIGN_TYPES } from 'shared/constants/campaign.js';
import { CAMPAIGNS_EVENTS } from 'dashboard/helper/AnalyticsHelper/events.js';

import KaptionEnhancedCampaignNewStep1 from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/KaptionEnhancedCampaignNewStep1.vue';
import KaptionEnhancedCampaignNewStep2 from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/KaptionEnhancedCampaignNewStep2.vue';
import KaptionEnhancedCampaignNewStep3 from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/KaptionEnhancedCampaignNewStep3.vue';
// NOTICE: KAPTION-INTEGRATION - Card title input (tap_target_configuration)
import Input from 'dashboard/components-next/input/Input.vue';
import ContactAPI from 'dashboard/api/contacts';
import { convertEmojiShortcodes } from 'shared/helpers/emojiShortcodes';
// NOTICE: KAPTION-INTEGRATION
import { sanitizeAudience } from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/audienceHelpers';
// NOTICE: KAPTION-INTEGRATION - Campaign assistant routing
import KaptionCampaignAssistantSelector from 'dashboard/components/kaption/KaptionCampaignAssistantSelector.vue';
// NOTICE: KAPTION-INTEGRATION - Campaign skip-assistant toggle (feature-flag gated)
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
import { useFeaturePermissions } from 'dashboard/composables/useFeaturePermissions';

const router = useRouter();
const store = useStore();
const { t } = useI18n();
const { isKaptionFlagEnabled } = useKaptionFeatureFlags();
const { canUseFeature } = useFeaturePermissions();

if (!canUseFeature('campaign_broadcasts')) {
  router.replace({ name: 'campaigns_whatsapp_index' });
}

const isLoading = ref(false);

// Steps logic - which step is currently expanded
const expandedStep = ref(1);

// Form state
const formState = ref({
  // Step 1: Campaign Details + Inbox + Audience
  title: '',
  scheduledAt: null,
  inboxId: null,
  selectedAudience: [],
  contactCount: null,

  // NOTICE: KAPTION-INTEGRATION - Campaign assistant routing
  assistantId: null,

  // NOTICE: KAPTION-INTEGRATION - Deliver replies straight to inbox, bypassing the assistant
  skipAssistant: false,

  // Step 2: Template & Parameters
  templateId: null,
  variableBindings: {},
  allVariablesMapped: false,

  // NOTICE: KAPTION-INTEGRATION - Tappable card title (tap_target_configuration)
  tapTargetTitle: '',
});

// Sample contacts for Step 3 preview (up to 5)
const sampleContacts = ref([]);

// Validation
const isStep1Valid = computed(() => {
  return (
    formState.value.title &&
    formState.value.title.trim().length > 0 &&
    formState.value.scheduledAt &&
    formState.value.inboxId &&
    formState.value.selectedAudience.length > 0 &&
    formState.value.contactCount > 0
  );
});

const isStep2Valid = computed(() => {
  return formState.value.templateId && formState.value.allVariablesMapped;
});

// Store getters
const getFilteredWhatsAppTemplates = useMapGetter(
  'inboxes/getFilteredWhatsAppTemplates'
);
const labels = useMapGetter('labels/getLabels');
const inboxes = useMapGetter('inboxes/getWhatsAppInboxes');

// Computed summaries for collapsed states
const step1Summary = computed(() => {
  if (!isStep1Valid.value) return null;

  const inbox = inboxes.value.find(i => i.id === formState.value.inboxId);
  const label = labels.value.find(
    l => l.id === formState.value.selectedAudience[0]
  );

  const scheduledDate = new Date(formState.value.scheduledAt);
  const now = new Date();
  const isImmediate = Math.abs(scheduledDate.getTime() - now.getTime()) < 60000;

  return {
    title: formState.value.title,
    schedule: isImmediate
      ? 'Now'
      : scheduledDate.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        }),
    inbox: inbox?.name || 'Unknown',
    audience: label?.title || 'Unknown',
    contactCount: formState.value.contactCount,
  };
});

const step2Summary = computed(() => {
  if (!isStep2Valid.value) return null;

  const templates = getFilteredWhatsAppTemplates.value(formState.value.inboxId);
  const template = templates?.find(
    tmpl => tmpl.id === formState.value.templateId
  );

  return {
    templateName: template?.name || 'Unknown',
    language: template?.language || 'en',
  };
});

// NOTICE: KAPTION-INTEGRATION - Selected template + URL-button detection.
// The tappable card title (tap_target_configuration) only has an effect when the
// template has a tracked kapt.io URL button; mirror KaptionDirectSendPage.
const selectedTemplate = computed(() => {
  if (!formState.value.inboxId || !formState.value.templateId) return null;
  const templates = getFilteredWhatsAppTemplates.value(formState.value.inboxId);
  return (
    templates?.find(tmpl => tmpl.id === formState.value.templateId) || null
  );
});

const hasUrlButton = computed(() => {
  if (!selectedTemplate.value) return false;
  const buttonsComp = selectedTemplate.value.components?.find(
    c => c.type === 'BUTTONS'
  );
  if (!buttonsComp) return false;
  return (buttonsComp.buttons || []).some(b => b.type === 'URL');
});

// Fetch sample contacts for preview
const fetchSampleContacts = async () => {
  if (!formState.value.selectedAudience.length) {
    sampleContacts.value = [];
    return;
  }

  try {
    const labelId = formState.value.selectedAudience[0];
    const label = labels.value.find(l => l.id === labelId);

    if (!label) {
      sampleContacts.value = [];
      return;
    }

    const response = await ContactAPI.get(1, 'created_at', label.title);

    if (
      response.data &&
      response.data.payload &&
      response.data.payload.length > 0
    ) {
      sampleContacts.value = response.data.payload.slice(0, 5);
    } else {
      sampleContacts.value = [];
    }
  } catch {
    sampleContacts.value = [];
  }
};

// Actions
const handleCancel = () => {
  router.push({ name: 'campaigns_whatsapp_index' });
};

const submitCampaign = async () => {
  if (!canUseFeature('campaign_broadcasts')) return;
  isLoading.value = true;
  // eslint-disable-next-line no-console
  console.log(
    '[KAPTION:BuilderPage] submitCampaign() formState.selectedAudience:',
    JSON.stringify(formState.value.selectedAudience)
  );

  try {
    const {
      inboxId,
      templateId,
      selectedAudience,
      variableBindings,
      title,
      scheduledAt,
    } = formState.value;

    const templates = getFilteredWhatsAppTemplates.value(inboxId);
    const template = templates.find(tmpl => tmpl.id === templateId);

    if (!template) throw new Error('Template not found');

    // Build proper enhanced processed_params with body/header/buttons structure
    const processedParams = {};

    if (variableBindings.body) {
      processedParams.body = {};
      Object.entries(variableBindings.body).forEach(([key, value]) => {
        const cleanKey = key.replace(/^{{|}}$/g, '').trim();
        // Safety net: convert any remaining emoji shortcodes before save
        processedParams.body[cleanKey] = convertEmojiShortcodes(value);
      });
    }

    if (variableBindings.header) {
      processedParams.header = { ...variableBindings.header };
    }

    if (variableBindings.buttons) {
      processedParams.buttons = [...variableBindings.buttons];
    }

    const campaignScheduledAt = new Date(scheduledAt).toISOString();

    const campaignDetails = {
      title,
      inbox_id: inboxId,
      scheduled_at: campaignScheduledAt,
      message: 'Template Message',
      template_params: {
        name: template.name,
        namespace: template.namespace,
        category: template.category,
        language: template.language,
        processed_params: processedParams,
      },
      // NOTICE: KAPTION-INTEGRATION
      // sanitizeAudience unwraps nested {id:{id,type}} from Vue reactivity cycles
      // and preserves the correct type (Label vs Segment)
      audience: sanitizeAudience(selectedAudience),
      // NOTICE: KAPTION-INTEGRATION - Skip-assistant delivery (open + unassigned)
      trigger_rules: {
        skip_assistant: formState.value.skipAssistant,
        // NOTICE: KAPTION-INTEGRATION - Tappable card title; backend treats blank as no-op
        tap_target_title: formState.value.tapTargetTitle?.trim() || null,
      },
      // NOTICE: KAPTION-INTEGRATION - Campaign assistant routing
      ...(formState.value.assistantId && {
        kaption_assistant_id: formState.value.assistantId,
      }),
    };

    await store.dispatch('campaigns/create', campaignDetails);

    useTrack(CAMPAIGNS_EVENTS.CREATE_CAMPAIGN, {
      type: CAMPAIGN_TYPES.ONE_OFF,
      source: 'enhanced_builder',
    });

    useAlert(t('CAMPAIGN.WHATSAPP.CREATE.FORM.API.SUCCESS_MESSAGE'));
    router.push({ name: 'campaigns_whatsapp_index' });
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t('CAMPAIGN.WHATSAPP.CREATE.FORM.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  } finally {
    isLoading.value = false;
  }
};

// Step navigation
const completeStep1 = async () => {
  if (isStep1Valid.value) {
    expandedStep.value = 2;
    await nextTick();
  }
};

const completeStep2 = async () => {
  if (isStep2Valid.value) {
    expandedStep.value = 3;
    await fetchSampleContacts();
  }
};

const editStep = stepNumber => {
  expandedStep.value = stepNumber;
};
</script>

<template>
  <div
    class="wp-card wp-campaign-fullpage flex flex-col h-full bg-n-background -mx-6"
  >
    <!-- Header -->
    <header
      class="relative flex items-center px-8 py-4 border-b border-n-weak bg-n-solid-2"
    >
      <button
        type="button"
        class="flex items-center gap-2 text-n-slate-11 hover:text-n-slate-12 transition-colors"
        @click="handleCancel"
      >
        <span class="i-lucide-arrow-left w-5 h-5" />
        <span class="text-sm font-medium">{{
          t('CAMPAIGN.WHATSAPP.CREATE.BUILDER.HEADER.BACK_TO_CAMPAIGNS')
        }}</span>
      </button>
      <h1
        class="absolute left-1/2 -translate-x-1/2 text-lg font-semibold text-n-slate-12"
      >
        {{ t('CAMPAIGN.WHATSAPP.CREATE.BUILDER.HEADER.TITLE') }}
      </h1>
    </header>

    <!-- Main Content -->
    <main class="flex-1 overflow-y-auto px-6">
      <div class="py-8 px-2">
        <div class="flex flex-col gap-4">
          <!-- Step 1: Campaign Details -->
          <div
            class="rounded-xl border overflow-hidden step-section"
            :class="[
              expandedStep === 1
                ? 'border-n-brand bg-n-solid-2 step-expanded'
                : 'border-n-weak bg-n-solid-2 hover:border-n-slate-6 step-collapsed',
            ]"
          >
            <!-- Collapsed Header -->
            <button
              v-if="expandedStep !== 1 && isStep1Valid"
              type="button"
              class="w-full px-5 py-4 flex items-center gap-4 text-left group"
              @click="editStep(1)"
            >
              <div
                class="w-7 h-7 rounded-full bg-n-brand text-white flex items-center justify-center text-sm font-medium flex-shrink-0"
              >
                <span class="i-lucide-check w-4 h-4" />
              </div>
              <div class="flex-1 flex items-center gap-4 min-w-0">
                <span
                  class="font-semibold text-n-slate-12 truncate max-w-[220px]"
                >
                  {{ step1Summary?.title }}
                </span>
                <!-- eslint-disable vue/no-bare-strings-in-template -->
                <span class="text-n-slate-9">•</span>
                <!-- eslint-enable vue/no-bare-strings-in-template -->
                <span class="text-n-slate-11 truncate">
                  {{ step1Summary?.schedule }}
                </span>
                <!-- eslint-disable vue/no-bare-strings-in-template -->
                <span class="text-n-slate-9">•</span>
                <!-- eslint-enable vue/no-bare-strings-in-template -->
                <span class="text-n-slate-11 truncate">
                  {{ step1Summary?.inbox }}
                </span>
                <!-- eslint-disable vue/no-bare-strings-in-template -->
                <span class="text-n-slate-9">•</span>
                <span class="text-n-slate-11 truncate">
                  {{ step1Summary?.audience }}
                  ({{ step1Summary?.contactCount }})
                </span>
                <!-- eslint-enable vue/no-bare-strings-in-template -->
              </div>
              <span
                class="i-lucide-pencil w-4 h-4 text-n-slate-11 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
              />
            </button>

            <!-- Expanded Content -->
            <div v-if="expandedStep === 1" class="step-content">
              <!-- Header -->
              <div class="px-5 py-4 border-b border-n-weak">
                <div class="flex items-center gap-3">
                  <!-- eslint-disable vue/no-bare-strings-in-template -->
                  <div
                    class="w-7 h-7 rounded-full bg-n-brand text-white flex items-center justify-center text-sm font-medium"
                  >
                    1
                  </div>
                  <!-- eslint-enable vue/no-bare-strings-in-template -->
                  <h2 class="text-base font-semibold text-n-slate-12">
                    {{
                      t('CAMPAIGN.WHATSAPP.CREATE.BUILDER.STEPS.STEP_1_TITLE')
                    }}
                  </h2>
                </div>
              </div>

              <!-- Form -->
              <div class="p-5">
                <KaptionEnhancedCampaignNewStep1
                  :title="formState.title"
                  :scheduled-at="formState.scheduledAt"
                  :inbox-id="formState.inboxId"
                  :selected-audience="formState.selectedAudience"
                  @update:title="formState.title = $event"
                  @update:scheduled-at="formState.scheduledAt = $event"
                  @update:inbox-id="formState.inboxId = $event"
                  @update:selected-audience="
                    formState.selectedAudience = $event
                  "
                  @update:contact-count="formState.contactCount = $event"
                />
                <!-- NOTICE: KAPTION-INTEGRATION - Campaign assistant routing -->
                <KaptionCampaignAssistantSelector
                  v-model="formState.assistantId"
                />
                <!-- NOTICE: KAPTION-INTEGRATION - Deliver straight to inbox (skip assistant) -->
                <div
                  v-if="isKaptionFlagEnabled('KAPTION_CAMPAIGN_SKIP_ASSISTANT')"
                  class="flex flex-col gap-1 mt-4"
                >
                  <label
                    class="flex items-center gap-2 text-sm font-medium text-n-slate-12 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      :checked="formState.skipAssistant"
                      class="h-4 w-4 rounded border-n-weak"
                      @change="formState.skipAssistant = $event.target.checked"
                    />
                    {{
                      t(
                        'CAMPAIGN.WHATSAPP.CREATE.BUILDER.STEPS.SKIP_ASSISTANT_LABEL'
                      )
                    }}
                  </label>
                  <p class="text-xs text-n-slate-11 ml-6">
                    {{
                      t(
                        'CAMPAIGN.WHATSAPP.CREATE.BUILDER.STEPS.SKIP_ASSISTANT_HINT'
                      )
                    }}
                  </p>
                </div>
              </div>

              <!-- Footer -->
              <div
                class="px-5 py-4 border-t border-n-weak flex items-center justify-end"
              >
                <button
                  type="button"
                  class="px-5 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2"
                  :class="[
                    isStep1Valid
                      ? 'bg-n-brand text-white hover:bg-n-brand-hover shadow-sm'
                      : 'bg-n-slate-3 text-n-slate-10 cursor-not-allowed',
                  ]"
                  :disabled="!isStep1Valid"
                  @click="completeStep1"
                >
                  {{ t('CAMPAIGN.WHATSAPP.CREATE.BUILDER.STEPS.CONTINUE') }}
                  <span class="i-lucide-arrow-down w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          <!-- Step 2: Template & Parameters -->
          <div
            class="rounded-xl border overflow-hidden step-section"
            :class="[
              expandedStep === 2
                ? 'border-n-brand bg-n-solid-2 step-expanded'
                : expandedStep > 2 || (expandedStep < 2 && isStep1Valid)
                  ? 'border-n-weak bg-n-solid-2 hover:border-n-slate-6 step-collapsed'
                  : 'border-n-weak bg-n-solid-2 opacity-50 step-collapsed',
            ]"
          >
            <!-- Collapsed Header - Completed -->
            <button
              v-if="expandedStep !== 2 && isStep2Valid"
              type="button"
              class="w-full px-5 py-4 flex items-center gap-4 text-left group"
              @click="editStep(2)"
            >
              <div
                class="w-7 h-7 rounded-full bg-n-brand text-white flex items-center justify-center text-sm font-medium flex-shrink-0"
              >
                <span class="i-lucide-check w-4 h-4" />
              </div>
              <div class="flex-1 flex items-center gap-4 min-w-0">
                <span class="font-semibold text-n-slate-12">{{
                  t(
                    'CAMPAIGN.WHATSAPP.CREATE.BUILDER.STEP_SUMMARY.TEMPLATE_LABEL'
                  )
                }}</span>
                <!-- eslint-disable vue/no-bare-strings-in-template -->
                <span class="text-n-slate-11 truncate">
                  {{ step2Summary?.templateName }}
                  ({{ step2Summary?.language }})
                </span>
                <!-- eslint-enable vue/no-bare-strings-in-template -->
              </div>
              <span
                class="i-lucide-pencil w-4 h-4 text-n-slate-11 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
              />
            </button>

            <!-- Collapsed Header - Pending -->
            <div
              v-else-if="expandedStep !== 2"
              class="px-5 py-4 flex items-center gap-4"
            >
              <!-- eslint-disable vue/no-bare-strings-in-template -->
              <div
                class="w-7 h-7 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0"
                :class="
                  isStep1Valid
                    ? 'bg-n-slate-4 text-n-slate-11'
                    : 'bg-n-slate-3 text-n-slate-9'
                "
              >
                2
              </div>
              <!-- eslint-enable vue/no-bare-strings-in-template -->
              <span
                class="font-medium"
                :class="isStep1Valid ? 'text-n-slate-11' : 'text-n-slate-9'"
              >
                {{ t('CAMPAIGN.WHATSAPP.CREATE.BUILDER.STEPS.STEP_2_TITLE') }}
              </span>
            </div>

            <!-- Expanded Content -->
            <div v-if="expandedStep === 2" class="step-content">
              <!-- Header -->
              <div class="px-5 py-4 border-b border-n-weak">
                <div class="flex items-center gap-3">
                  <!-- eslint-disable vue/no-bare-strings-in-template -->
                  <div
                    class="w-7 h-7 rounded-full bg-n-brand text-white flex items-center justify-center text-sm font-medium"
                  >
                    2
                  </div>
                  <!-- eslint-enable vue/no-bare-strings-in-template -->
                  <h2 class="text-base font-semibold text-n-slate-12">
                    {{
                      t('CAMPAIGN.WHATSAPP.CREATE.BUILDER.STEPS.STEP_2_TITLE')
                    }}
                  </h2>
                </div>
              </div>

              <!-- Form -->
              <div class="p-5">
                <KaptionEnhancedCampaignNewStep2
                  :inbox-id="formState.inboxId"
                  :template-id="formState.templateId"
                  :selected-audience="formState.selectedAudience"
                  :variable-bindings="formState.variableBindings"
                  @update:template-id="formState.templateId = $event"
                  @update:variable-bindings="
                    formState.variableBindings = $event
                  "
                  @update:all-variables-mapped="
                    formState.allVariablesMapped = $event
                  "
                />
                <!-- NOTICE: KAPTION-INTEGRATION - Card title (tap_target_configuration).
                     Renders the template as a tappable card whose whole surface
                     opens the tracked link. Only shown for templates with a URL
                     button (backend no-ops otherwise). -->
                <div v-if="hasUrlButton" class="flex flex-col gap-1 mt-4">
                  <label class="mb-0.5 text-sm font-medium text-n-slate-12">
                    {{
                      t(
                        'CAMPAIGN.WHATSAPP.CREATE.BUILDER.TAP_TARGET_TITLE_LABEL'
                      )
                    }}
                  </label>
                  <Input
                    v-model="formState.tapTargetTitle"
                    type="text"
                    :placeholder="
                      t(
                        'CAMPAIGN.WHATSAPP.CREATE.BUILDER.TAP_TARGET_TITLE_PLACEHOLDER'
                      )
                    "
                  />
                  <p class="text-xs text-n-slate-11">
                    {{
                      t(
                        'CAMPAIGN.WHATSAPP.CREATE.BUILDER.TAP_TARGET_TITLE_HINT'
                      )
                    }}
                  </p>
                </div>
              </div>

              <!-- Footer -->
              <div
                class="px-5 py-4 border-t border-n-weak flex items-center justify-end"
              >
                <button
                  type="button"
                  class="px-5 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2"
                  :class="[
                    isStep2Valid
                      ? 'bg-n-brand text-white hover:bg-n-brand-hover shadow-sm'
                      : 'bg-n-slate-3 text-n-slate-10 cursor-not-allowed',
                  ]"
                  :disabled="!isStep2Valid"
                  @click="completeStep2"
                >
                  {{ t('CAMPAIGN.WHATSAPP.CREATE.BUILDER.STEPS.CONTINUE') }}
                  <span class="i-lucide-arrow-down w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          <!-- Step 3: Confirm & Send -->
          <div
            class="rounded-xl border overflow-hidden step-section"
            :class="[
              expandedStep === 3
                ? 'border-n-brand bg-n-solid-2 step-expanded'
                : 'border-n-weak bg-n-solid-2 opacity-50 step-collapsed',
            ]"
          >
            <!-- Collapsed Header - Pending -->
            <div
              v-if="expandedStep !== 3"
              class="px-5 py-4 flex items-center gap-4"
            >
              <!-- eslint-disable vue/no-bare-strings-in-template -->
              <div
                class="w-7 h-7 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0"
                :class="
                  isStep2Valid
                    ? 'bg-n-slate-4 text-n-slate-11'
                    : 'bg-n-slate-3 text-n-slate-9'
                "
              >
                3
              </div>
              <!-- eslint-enable vue/no-bare-strings-in-template -->
              <span
                class="font-medium"
                :class="isStep2Valid ? 'text-n-slate-11' : 'text-n-slate-9'"
              >
                {{ t('CAMPAIGN.WHATSAPP.CREATE.BUILDER.STEPS.STEP_3_TITLE') }}
              </span>
            </div>

            <!-- Expanded Content -->
            <div v-if="expandedStep === 3" class="step-content">
              <!-- Header -->
              <div class="px-5 py-4 border-b border-n-weak">
                <div class="flex items-center gap-3">
                  <!-- eslint-disable vue/no-bare-strings-in-template -->
                  <div
                    class="w-7 h-7 rounded-full bg-n-brand text-white flex items-center justify-center text-sm font-medium"
                  >
                    3
                  </div>
                  <!-- eslint-enable vue/no-bare-strings-in-template -->
                  <h2 class="text-base font-semibold text-n-slate-12">
                    {{
                      t('CAMPAIGN.WHATSAPP.CREATE.BUILDER.STEPS.STEP_3_TITLE')
                    }}
                  </h2>
                </div>
              </div>

              <!-- Preview Content -->
              <div class="p-5">
                <KaptionEnhancedCampaignNewStep3
                  :title="formState.title"
                  :tap-target-title="formState.tapTargetTitle"
                  :scheduled-at="formState.scheduledAt"
                  :inbox-id="formState.inboxId"
                  :template-id="formState.templateId"
                  :selected-audience="formState.selectedAudience"
                  :contact-count="formState.contactCount"
                  :variable-bindings="formState.variableBindings"
                  :sample-contacts="sampleContacts"
                />
              </div>

              <!-- Footer -->
              <div
                class="px-5 py-4 border-t border-n-weak flex items-center justify-between"
              >
                <button
                  type="button"
                  class="text-sm text-n-slate-11 hover:text-n-slate-12 px-4 py-2.5 rounded-lg hover:bg-n-alpha-black2 transition-colors"
                  @click="handleCancel"
                >
                  {{ t('DIALOG.BUTTONS.CANCEL') }}
                </button>

                <button
                  type="button"
                  class="px-6 py-2.5 rounded-lg text-sm font-medium text-white bg-n-brand hover:bg-n-brand-hover transition-all shadow-sm flex items-center gap-2"
                  :disabled="isLoading"
                  @click="submitCampaign"
                >
                  <span
                    v-if="isLoading"
                    class="animate-spin h-4 w-4 border-2 border-white border-r-transparent rounded-full"
                  />
                  <span class="i-lucide-send w-4 h-4" />
                  {{
                    t('CAMPAIGN.WHATSAPP.CREATE.BUILDER.STEPS.SEND_CAMPAIGN')
                  }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<style scoped>
/* NOTICE: KAPTION-INTEGRATION */
/* Gmail-style step animations */
.step-section {
  transition:
    all 0.35s cubic-bezier(0.4, 0, 0.2, 1),
    border-color 0.2s ease,
    opacity 0.2s ease;
}

.step-expanded {
  animation: stepExpand 0.35s cubic-bezier(0.4, 0, 0.2, 1) forwards;
}

.step-collapsed {
  animation: stepCollapse 0.25s cubic-bezier(0.4, 0, 0.2, 1) forwards;
}

@keyframes stepExpand {
  0% {
    opacity: 0.8;
    transform: translateY(-8px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes stepCollapse {
  0% {
    opacity: 1;
    transform: scale(1);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

/* Smooth content transitions */
.step-content {
  animation: contentFadeIn 0.3s ease-out forwards;
}

@keyframes contentFadeIn {
  0% {
    opacity: 0;
    transform: translateY(-4px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Hover effect for collapsed completed steps */
.step-collapsed:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}
</style>
